import { Box, Flex, Skeleton, Text } from '@chakra-ui/react';
import { CartTotalWithMagnify, DetailProduct } from '@diamond/shared/types';
import { Button } from '@diamond/shared/ui';
import { buyerCurrencyFormatter } from '@diamond/shared/utils';

import { PromoSection } from '../../common/add-to-cart-popup/PromoSection';
import { VoucherSection } from '../../common/add-to-cart-popup/VoucherSection';
import type { OrderCounterState } from './useOrderCounter';

interface SharedProps {
  hook: OrderCounterState;
}

/**
 * Promo + Voucher sections rendered together (shared between desktop and mobile).
 */
export function PromoVoucherSections({
  hook,
  productData,
}: SharedProps & { productData?: DetailProduct }) {
  if (!hook.isPromoVoucherPreviewEnabled) return null;

  return (
    <>
      <PromoSection
        offers={hook.promoOffers}
        effectiveMagnifiedQty={hook.effectiveMagnifiedQty}
        maxAllowedQuantity={hook.maxAllowedQuantity}
        onUseQuantity={hook.handleUsePromoQuantity}
      />
      {hook.isVoucherDisplayEnabled && (
        <VoucherSection
          vouchers={hook.voucherOffers}
          itemCode={productData?.item_code}
          effectiveMagnifiedQty={hook.effectiveMagnifiedQty}
          maxAllowedQuantity={hook.maxAllowedQuantity}
          onUseQuantity={hook.handleUseQuantity}
          onSelectVoucher={hook.handleSelectVoucher}
        />
      )}
    </>
  );
}

/**
 * Price row: line-through original + "Hemat" when discount, then price box + Keranjang button.
 */
export function PriceRow({
  hook,
  cartTotalData,
  isCartTotalFetching,
  priceFontSize,
  priceBoxMinH,
  stackOnSmall = false,
}: SharedProps & {
  cartTotalData?: CartTotalWithMagnify;
  isCartTotalFetching?: boolean;
  priceFontSize: string;
  priceBoxMinH: number;
  /** Stack the price box above the button on narrow screens so the nominal isn't clipped. */
  stackOnSmall?: boolean;
}) {
  const direction = stackOnSmall
    ? ({ base: 'column', sm: 'row' } as const)
    : 'row';
  const buttonWidth = stackOnSmall
    ? ({ base: 'full', sm: 'auto' } as const)
    : 'auto';

  return (
    <>
      {hook.isMagnifiedPriceDiff && cartTotalData && (
        <Box mb={1}>
          <Text as="span" color="gray.75" textDecoration="line-through">
            {buyerCurrencyFormatter(
              cartTotalData.originalPriceMagnify.magnifiedValue
            )}
          </Text>
          <Text as="span" ml="2" color="#FF0000">
            Hemat{' '}
            {buyerCurrencyFormatter(
              cartTotalData.savedPriceMagnify.magnifiedValue
            )}
          </Text>
        </Box>
      )}
      <Flex align="stretch" direction={direction} gap={3} mt={2}>
        <Skeleton
          borderRadius="lg"
          isLoaded={!isCartTotalFetching}
          flex="1"
          minW={0}
        >
          <Box
            px={4}
            bgColor="#FAFAFA"
            rounded="lg"
            h="full"
            minH={priceBoxMinH}
            display="flex"
            alignItems="center"
            overflow="hidden"
          >
            <Flex align="baseline" gap={1} minW={0}>
              <Text color="blue" fontWeight="bold" fontSize="xs" flexShrink={0}>
                Rp
              </Text>
              <Text
                color="blue"
                fontWeight="bold"
                fontSize={priceFontSize}
                noOfLines={1}
              >
                {hook.finalPriceFormatted}
              </Text>
            </Flex>
          </Box>
        </Skeleton>
        <Button
          bgColor="blue"
          color="white"
          variant="solid"
          id="button-add-cart"
          onClick={hook.handleKeranjangClick}
          isDisabled={hook.isAddDisabled}
          isLoading={isCartTotalFetching && !hook.isPriceZero}
          flexShrink={0}
          px={6}
          h="auto"
          minH={priceBoxMinH}
          w={buttonWidth}
          alignSelf="stretch"
        >
          {hook.keranjangLabel}
        </Button>
      </Flex>
    </>
  );
}
